import * as yup from 'yup'
import dayjs from 'dayjs'

import { gtvOwnerId } from '../ui/getData'

export type UserAuthResponse = {
  avatar: string
  email: string
  id: string
  name: string
  company: {
    id: number
    name: string
  }
  roles: string[]
}

export const CampaignInputSchema = yup.object().shape({
  name: yup.string().required('This field is required'),
  owner_id: yup.number().nullable().required('This field is required'),
  advertiser_id: yup.number().nullable().required('This field is required'),
  delivery_type: yup.string().required('This field is required'),
  content_category_id: yup.number().nullable().optional(),
  hubspot_deal_id: yup
    .string()
    .optional()
    .when('owner_id', {
      is: gtvOwnerId,
      then: (schema) => schema.required('This field is required'),
      otherwise: (schema) => schema.notRequired()
    })
})

export type CampaignInput = yup.InferType<typeof CampaignInputSchema>

export enum CampaignStatus {
  Draft = 'draft',
  // Booked = 'booked',
  // Reserved = 'reserved',
  Scheduled = 'scheduled',
  Deleted = 'deleted',
  Live = 'live',
  Dark = 'dark',
  Completed = 'completed'
}

export type CampaignResponse = {
  id: number
  name: string
  status: CampaignStatus
  owner: Owner
  advertiser: Advertiser
  hubspot_deal_id: string
  delivery_type: DeliveryType
  content_category: ContentCategory | null
  calculated_duration: {
    starts_at: string | null
    ends_at: string | null
  }
  calculated_budget: number
  flights: Flight[]
}

export type Owner = {
  id: number
  name: string
}

export type Advertiser = {
  id: number
  name: string
}

export type ContentCategory = {
  id: number
  name: string
  parent_id: number | null
}

export type AdvertiserInput = {
  name: string
}

export type ErrorResponse = {
  status: number
  message: string
  context: string[]
}

export enum DeliveryType {
  Impressions = 'impressions',
  PlayFrequency = 'play_frequency'
}

export type FlightImpressions = {
  type: DeliveryType.Impressions
  impression_target: number
  budget: number
  cpm: number
}

export type FlightFrequency = {
  type: DeliveryType.PlayFrequency
  play_frequency_seconds: number
  budget: number
}

export type FlightTargeting = {
  stores: {
    id: number
    name: string
    installed: boolean
    banner: {
      id: number
      name: string
    }
  }[]
  zones: {
    id: number
    name: string
  }[]
  placements: {
    id: number
    name: string
  }[]
  filters?: {
    banner_ids?: number[]
    states?: string[]
  }
}

export type Flight = {
  id: number
  name: string
  description: string
  status: CampaignStatus
  duration: {
    starts_at: string | null
    ends_at: string | null
  }
  is_guaranteed: boolean
  delivery: FlightImpressions | FlightFrequency
  targeting: FlightTargeting
  time_zone: string
  creatives: CreativeUploadResponse[]
}

const durationSchema = yup.object().shape({
  starts_at: yup.string().nullable(),
  ends_at: yup
    .string()
    .nullable()
    .test('is-after-start', 'The end date must be after the start date', function (ends_at) {
      const { starts_at } = this.parent
      if (!starts_at || !ends_at) return true
      return dayjs(ends_at).isAfter(dayjs(starts_at))
    })
})

export const DurationInputSchema = yup.object().shape({
  name: yup.string().required('This field is required'),
  duration: durationSchema
})
export type DurationInput = yup.InferType<typeof DurationInputSchema>

export const FlightInputSchema = yup.object().shape({
  name: yup.string().required('This field is required'),
  description: yup.string().optional(),
  duration: yup.object().shape({
    starts_at: yup.string().required('This field is required'),
    ends_at: yup
      .string()
      .required('This field is required')
      .test('is-after-start', 'The end date must be after the start date', function (ends_at) {
        const { starts_at } = this.parent
        if (!starts_at || !ends_at) return true
        return dayjs(ends_at).isAfter(dayjs(starts_at))
      })
  }),
  is_guaranteed: yup.boolean().required('This field is required'),
  delivery: yup.object().shape({
    type: yup.string().required('Delivery type is required'),
    budget: yup
      .number()
      .nullable()
      .transform((value, originalValue) => {
        return originalValue === '' || Number.isNaN(value) ? undefined : value
      })
      .required('This field is required')
      .min(0, 'Budget must be positive'),
    impression_target: yup
      .number()
      .nullable()
      .transform((value, originalValue) => {
        return originalValue === '' || Number.isNaN(value) ? undefined : value
      })
      .when('type', {
        is: DeliveryType.Impressions,
        then: (schema) =>
          schema
            .required('This field is required')
            .positive('Impression target must be greater than 0'),
        otherwise: (schema) => schema.notRequired()
      }),
    play_frequency_seconds: yup
      .number()
      .nullable()
      .transform((value, originalValue) => {
        return originalValue === '' || Number.isNaN(value) ? undefined : value
      })
      .when('type', {
        is: DeliveryType.PlayFrequency,
        then: (schema) =>
          schema
            .required('This field is required')
            .min(15, 'Play frequency must be at least 15 seconds'),
        otherwise: (schema) => schema.notRequired()
      }),
    cpm: yup
      .number()
      .nullable()
      .transform((value, originalValue) => {
        return originalValue === '' || Number.isNaN(value) ? undefined : value
      })
      .optional()
  })
})

export type FlightInput = yup.InferType<typeof FlightInputSchema>

export const CloneFlightInputSchema = yup.object().shape({
  duration: yup.object().shape({
    starts_at: yup.string().required('This field is required'),
    ends_at: yup
      .string()
      .required('This field is required')
      .test('is-after-start', 'The end date must be after the start date', function (ends_at) {
        const { starts_at } = this.parent
        if (!starts_at || !ends_at) return true
        return dayjs(ends_at).isAfter(dayjs(starts_at))
      })
  })
})

export type CloneFlightInput = yup.InferType<typeof CloneFlightInputSchema>

export type Store = {
  id: number
  name: string
  banner: {
    id: number
    name: string
  }
  address: {
    street: string | null
    city: string | null
    state: string | null
    postal_code: string | null
  }
}

export type Placement = {
  id: number
  name: string
  store_count: number
  screen_count: number
}
export type Zone = {
  id: number
  name: string
  store_count: number
  screen_count: number
  aspect_ratio: {
    ratio: string
    orientation: string
    max_creative_duration_ms: number
    minimum_resolution: {
      width: number
      height: number
    }
  }
  placements: Placement[]
}
export type ZoneResponse = {
  store_count: number
  screen_count: number
  zone_count: number
  placement_count: number
  zones: Zone[]
}
export type PreviewResponse = {
  store_count: number
  screen_count: number
  zone_count: number
  placement_count: number
  zones: Zone[]
}

export type CreativePreviewInput = {
  stores: {
    id: number
  }[]
  zones: {
    id: number
  }[]
  placements: {
    id: number
  }[]
}

export type FlightTargetFilters = {
  banner_ids: number[]
  states: string[]
}

export type FlightTargetSaveInput = CreativePreviewInput & {
  targeting_filter?: FlightTargetFilters
}

export type AspectRatio = {
  ratio: string
  orientation: string
  max_creative_duration_ms: number
  minimum_resolution: {
    width: number
    height: number
  }
}
export type CreativePreviewResponse = {
  store_count: number
  zone_count: number
  placement_count: number
  screen_count: number
  aspect_ratios: AspectRatio[]
}

export type FileUploadFields = {
  name: string
  startsAt: string | null
  endsAt: string | null
  aspectRatio: string
  durationMs: number
  contentCategoryId: number
}

export type SupportedAssetType = 'video/mp4' | 'image/png' | 'image/jpeg'

export type FileErrorType =
  | 'unsupported-file-type'
  | 'invalid-video-duration'
  | 'invalid-asset-aspect-ratio'
  | 'invalid-asset-aspect-ratio-for-store'
  | 'invalid-asset-resolution'

export type CreativeAsset = {
  type: string
  width: number
  height: number
  url: string
  aspect_ratio: {
    ratio: string
    orientation: string
  }
}

export type CreativeUploadResponse = {
  id: number
  name: string
  asset_duration_ms: number
  duration: {
    starts_at: string | null
    ends_at: string | null
  }
  calculated_duration: {
    starts_at: string | null
    ends_at: string | null
  }
  review_status: string
  content_category: string | null
  assets: CreativeAsset[]
}

export type CreativeUpdateInput = {
  id: number
  name: string
  starts_at: string | null
  ends_at: string | null
  content_category_id: number
}
